import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SignInPoint } from '@cloudsign/shared';
import { z } from 'zod';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { useCan } from '../../lib/session';
import { ErrorLine, ErrorPanel, fieldErrors } from '../../components/ErrorPanel';
import { Badge, Button, Card, CheckboxField, ConfirmButton, EmptyRow, PageHeader, SelectField, Spinner, TableWrap, TextField, Th } from '../../components/ui';
import { ALL_MODES, SiteSelect, openPdf, useActiveGroups } from './api';

const PointList = z.array(SignInPoint);
type Mode = (typeof ALL_MODES)[number];

function PointForm({ siteId, point, onDone }: { siteId: string; point: SignInPoint | null; onDone: () => void }) {
  const qc = useQueryClient();
  const groups = useActiveGroups();
  const [name, setName] = useState(point?.name ?? '');
  const [mode, setMode] = useState<Mode>(point?.mode ?? 'both');
  const [limited, setLimited] = useState(point?.groupIds != null);
  const [groupIds, setGroupIds] = useState<string[]>(point?.groupIds ?? []);
  const save = useMutation({
    mutationFn: () => {
      const body = { name, mode, groupIds: limited ? groupIds : null };
      return point
        ? api.patch(`/sign-in-points/${point.id}`, { body, schema: SignInPoint })
        : api.post('/sign-in-points', { body: { ...body, siteId }, schema: SignInPoint });
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['sign-in-points'] });
      onDone();
    },
  });
  const errs = fieldErrors(save.error);
  const toggle = (id: string) => setGroupIds((cur) => (cur.includes(id) ? cur.filter((g) => g !== id) : [...cur, id]));
  return (
    <Card title={point ? t('siteF.points.edit') : t('siteF.points.new')} className="mb-6">
      <form
        className="grid gap-4 sm:grid-cols-2"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <div className="sm:col-span-2">
          <ErrorLine error={save.error} />
        </div>
        <TextField label={t('siteF.points.name')} required maxLength={80} value={name} onChange={(e) => setName(e.target.value)} error={errs['name']} />
        <SelectField label={t('siteF.points.mode')} value={mode} onChange={(e) => setMode(e.target.value as Mode)}>
          {ALL_MODES.map((m) => (
            <option key={m} value={m}>
              {t(`siteF.points.mode.${m}`)}
            </option>
          ))}
        </SelectField>
        <fieldset className="sm:col-span-2">
          <legend className="mb-2 text-sm font-medium text-slate-800">{t('siteF.points.groups')}</legend>
          <CheckboxField label={t('siteF.points.limitGroups')} hint={t('siteF.points.limitGroupsHint')} checked={limited} onChange={(e) => setLimited(e.target.checked)} />
          {limited ? (
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              {(groups.data ?? []).map((g) => (
                <CheckboxField key={g.id} label={g.name} checked={groupIds.includes(g.id)} onChange={() => toggle(g.id)} />
              ))}
            </div>
          ) : null}
        </fieldset>
        <div className="flex gap-2 sm:col-span-2">
          <Button type="submit" variant="primary" disabled={save.isPending || name.trim() === '' || (limited && groupIds.length === 0)}>
            {t('common.save')}
          </Button>
          <Button onClick={onDone}>{t('common.cancel')}</Button>
        </div>
      </form>
    </Card>
  );
}

function PointRow({ point, groupNames, canManage, onEdit }: { point: SignInPoint; groupNames: Record<string, string>; canManage: boolean; onEdit: () => void }) {
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: ['sign-in-points'] });
  const newLink = useMutation({ mutationFn: () => api.post(`/sign-in-points/${point.id}/new-link`, { schema: SignInPoint }), onSuccess: refresh });
  const toggle = useMutation({ mutationFn: () => api.patch(`/sign-in-points/${point.id}`, { body: { active: !point.active }, schema: SignInPoint }), onSuccess: refresh });
  const poster = useMutation({
    mutationFn: () => api.download(`/sign-in-points/${point.id}/poster.pdf`),
    onSuccess: ({ blob, filename }) => openPdf(blob, filename ?? 'sign-in-point.pdf'),
  });
  const shown = point.groupIds === null ? t('siteF.points.allGroups') : point.groupIds.map((id) => groupNames[id] ?? '').filter(Boolean).join(', ');
  return (
    <tr className="border-t border-slate-100 align-top">
      <td className="px-4 py-3 font-medium">{point.name}</td>
      <td className="px-4 py-3">{t(`siteF.points.mode.${point.mode}`)}</td>
      <td className="px-4 py-3">{shown}</td>
      <td className="px-4 py-3">
        <Badge tone={point.active ? 'good' : 'neutral'}>{point.active ? t('siteF.points.active') : t('siteF.points.inactive')}</Badge>
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={() => poster.mutate()} disabled={poster.isPending} aria-label={t('siteF.points.posterFor', { name: point.name })}>
            {t('siteF.points.poster')}
          </Button>
          {canManage ? (
            <>
              <Button onClick={onEdit} aria-label={t('siteF.points.editFor', { name: point.name })}>
                {t('common.edit')}
              </Button>
              <Button onClick={() => toggle.mutate()} disabled={toggle.isPending}>
                {point.active ? t('siteF.points.deactivate') : t('siteF.points.activate')}
              </Button>
              <ConfirmButton label={t('siteF.points.newLink')} confirmLabel={t('siteF.points.newLinkConfirm')} pending={newLink.isPending} onConfirm={() => newLink.mutate()} />
            </>
          ) : null}
        </div>
        <ErrorLine error={poster.error ?? newLink.error ?? toggle.error} />
      </td>
    </tr>
  );
}

/** Portal page "Sign-in points" (sign-in-points.md): the static QR codes around a site. */
export function SignInPointsPage() {
  const canManage = useCan('sign_in_points.manage');
  const [siteId, setSiteId] = useState('');
  const [editing, setEditing] = useState<SignInPoint | 'new' | null>(null);
  const groups = useActiveGroups();
  const q = useQuery({
    queryKey: ['sign-in-points', siteId],
    enabled: siteId !== '',
    queryFn: () => api.get('/sign-in-points', { query: { siteId }, schema: PointList }),
  });
  const groupNames = Object.fromEntries((groups.data ?? []).map((g) => [g.id, g.name]));
  return (
    <>
      <PageHeader
        title={t('siteF.points.title')}
        description={t('siteF.points.description')}
        actions={
          canManage ? (
            <Button variant="primary" disabled={siteId === '' || editing !== null} onClick={() => setEditing('new')}>
              {t('siteF.points.add')}
            </Button>
          ) : null
        }
      />
      <div className="mb-4 max-w-sm">
        <SiteSelect value={siteId} onChange={setSiteId} />
      </div>
      {editing ? <PointForm key={editing === 'new' ? 'new' : editing.id} siteId={siteId} point={editing === 'new' ? null : editing} onDone={() => setEditing(null)} /> : null}
      {siteId === '' || q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <TableWrap caption={t('siteF.points.title')}>
          <thead className="border-b border-slate-200 bg-slate-50">
            <tr>
              <Th>{t('siteF.points.name')}</Th>
              <Th>{t('siteF.points.mode')}</Th>
              <Th>{t('siteF.points.groups')}</Th>
              <Th>{t('common.status')}</Th>
              <Th>{t('common.actions')}</Th>
            </tr>
          </thead>
          <tbody>
            {q.data.length === 0 ? <EmptyRow cols={5} /> : null}
            {q.data.map((p) => (
              <PointRow key={p.id} point={p} groupNames={groupNames} canManage={canManage} onEdit={() => setEditing(p)} />
            ))}
          </tbody>
        </TableWrap>
      )}
    </>
  );
}
